import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatApiError } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import {
  ChartPieSlice, ArrowLeft, Spinner, ArrowsClockwise, Hourglass,
  Receipt, UsersThree, Clock, MagnifyingGlass,
} from '@phosphor-icons/react';
import { toast } from 'sonner';

const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const _num = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const _fmt = (v) => _num(v).toLocaleString(undefined, { maximumFractionDigits: 0 });
const _isPaid = (s) => /paid|settled|success/i.test(String(s || ''));

const _daysAgo = (s) => {
  if (!s) return null;
  const d = new Date(String(s).replace(' ', 'T'));
  if (isNaN(d.getTime())) return null;
  return Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
};

export default function PaymentOrdersOverviewPage() {
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [cashierQ, setCashierQ] = useState('');
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState('');

  useEffect(() => {
    if (!isSuperAdmin) return;
    axios.get(`${API_URL}/api/entities`, { headers: authHeaders() })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        setEntities(list);
        if (list.length > 0) setEntityId(String(list[0]._id || list[0].id || list[0].middleware_id));
      })
      .catch(() => toast.error('Failed to load entities'));
  }, [isSuperAdmin]);

  const load = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = isSuperAdmin && entityId ? `?entity_id=${entityId}` : '';
      const { data } = await axios.get(`${API_URL}/api/cashiers/payment-orders${params}`, { headers: authHeaders() });
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(formatApiError(e, 'Failed to load payment orders'));
      setRows([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    if (isSuperAdmin && !entityId) return;
    load();
  }, [entityId, isSuperAdmin]); // eslint-disable-line react-hooks/exhaustive-deps

  const stats = useMemo(() => computeStats(rows), [rows]);
  const visibleCashiers = useMemo(() => {
    const ql = cashierQ.trim().toLowerCase();
    if (!ql) return stats.pendingCashiers;
    return stats.pendingCashiers.filter((c) => c.name.toLowerCase().includes(ql));
  }, [stats.pendingCashiers, cashierQ]);

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="payment-orders-overview-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#0A2540] flex items-center justify-center">
            <ChartPieSlice size={22} weight="duotone" className="text-[#C9A35B]" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#78716C] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
              Cashiers · Payment Orders
            </p>
            <h1 className="text-2xl font-bold text-[#1C1917] mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              Orders Overview
            </h1>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {isSuperAdmin && (
            <Select value={entityId} onValueChange={setEntityId}>
              <SelectTrigger
                className="h-9 min-w-[220px] text-xs font-semibold border-[#EDE6D8] bg-white"
                data-testid="po-overview-entity-select"
              >
                <SelectValue placeholder="Select entity…" />
              </SelectTrigger>
              <SelectContent>
                {entities.map((e) => (
                  <SelectItem key={e._id || e.id || e.middleware_id} value={String(e._id || e.id || e.middleware_id)}>
                    {e.entity_name || e.name || 'Unnamed entity'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <button
            type="button"
            onClick={() => navigate('/cashiers/payment-orders')}
            data-testid="po-overview-back"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold uppercase tracking-wider text-[#0A2540] hover:bg-[#FCFAF6] transition"
          >
            <ArrowLeft size={14} weight="bold" /> Back to Orders
          </button>
          <button
            type="button"
            onClick={() => { setRefreshing(true); load(true); }}
            disabled={refreshing}
            data-testid="po-overview-refresh"
            className="inline-flex items-center gap-2 h-9 px-3 rounded-lg border border-[#EDE6D8] bg-white text-xs font-semibold uppercase tracking-wider text-[#0A2540] hover:bg-[#FCFAF6] transition"
          >
            {refreshing ? <Spinner size={14} className="animate-spin" /> : <ArrowsClockwise size={14} weight="bold" />}
            Refresh
          </button>
        </div>
      </div>

      {loading ? (
        <div className="py-24 text-center">
          <Spinner size={24} className="animate-spin text-[#0A2540] inline" />
        </div>
      ) : (
        <>
          {/* KPI row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <KpiCard
              label="Pending Amount"
              value={stats.pendingByCurrency.length === 0 ? '—' : `${_fmt(stats.pendingByCurrency[0].amount)} ${stats.pendingByCurrency[0].currency}`}
              icon={Receipt}
              sub={stats.pendingByCurrency.slice(1).map((e) => `${_fmt(e.amount)} ${e.currency}`).join(' · ')}
            />
            <KpiCard label="Pending Orders" value={_fmt(stats.pendingCount)} icon={Hourglass} />
            <KpiCard
              label="Cashiers w/ Pending"
              value={_fmt(stats.pendingCashiers.length)}
              icon={UsersThree}
            />
            <KpiCard
              label="Oldest Pending"
              value={stats.oldestPendingDays === null ? '—' : `${stats.oldestPendingDays}d`}
              icon={Clock}
              sub={stats.oldestPendingDays !== null ? 'days awaiting payment' : ''}
            />
          </div>

          {/* Pending / Paid amount cards removed per user request */}

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Cashiers with pending, sorted by amount */}
            <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5" data-testid="po-overview-pending-cashiers">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
                  <UsersThree size={14} weight="fill" />
                </span>
                <p className="text-xs uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Cashiers with Pending · by Amount
                </p>
              </div>
              <div className="relative mb-3">
                <MagnifyingGlass size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#78716C]" />
                <input
                  value={cashierQ}
                  onChange={(e) => setCashierQ(e.target.value)}
                  placeholder="Search cashier…"
                  data-testid="po-overview-cashier-search"
                  className="w-full h-9 pl-8 pr-3 text-sm rounded-lg border border-[#EDE6D8] bg-white focus:outline-none focus:ring-2 focus:ring-[#0A2540]/20 placeholder:text-[#A8A29E]"
                />
              </div>
              {visibleCashiers.length === 0 ? (
                <p className="text-xs text-[#78716C] py-6 text-center">
                  {stats.pendingCashiers.length === 0 ? 'No pending orders. All settled.' : 'No cashier matches your search.'}
                </p>
              ) : (
                <div className="space-y-2.5 max-h-[312px] overflow-y-auto pr-2" data-testid="po-overview-cashier-list">
                  {visibleCashiers.map((c, i) => (
                    <div key={c.name} className="flex items-center gap-3" data-testid={`po-overview-cashier-${i}`}>
                      <span className="w-5 text-[11px] font-mono text-[#0A2540]">{i + 1}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0A2540] truncate">{c.name}</p>
                        <div className="h-1.5 rounded-full bg-[#FCFAF6] overflow-hidden mt-1">
                          <div className="h-full rounded-full bg-[#0A2540]" style={{ width: `${(c.amount / stats.maxPendingCashierAmount) * 100}%` }} />
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-mono font-semibold text-[#0A2540]">{_fmt(c.amount)} <span className="text-[10px] text-[#0A2540]">{c.currency}</span></p>
                        <p className="text-[10px] text-[#0A2540] font-mono">{c.count} order{c.count === 1 ? '' : 's'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Daily trend */}
            <div className="bg-white border border-[#EDE6D8] rounded-2xl p-5" data-testid="po-overview-trend">
              <div className="flex items-center gap-2 mb-3">
                <span className="w-7 h-7 rounded-lg flex items-center justify-center bg-[#0A2540]/10 text-[#0A2540]">
                  <Receipt size={14} weight="fill" />
                </span>
                <p className="text-xs uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
                  Orders Raised · Past 14 Days
                </p>
              </div>
              {stats.trend.every((d) => d.count === 0) ? (
                <p className="text-xs text-[#78716C] py-10 text-center">No orders raised in the last 14 days.</p>
              ) : (
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.trend} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#EDE6D8" vertical={false} />
                      <XAxis dataKey="day" tick={{ fontSize: 9, fontFamily: 'JetBrains Mono, monospace' }} axisLine={false} tickLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace' }} axisLine={false} tickLine={false} />
                      <Tooltip
                        cursor={{ fill: '#FCFAF6' }}
                        contentStyle={{ borderRadius: 12, border: '1px solid #EDE6D8', fontSize: 12, fontFamily: 'JetBrains Mono, monospace' }}
                      />
                      <Bar dataKey="count" name="Orders" fill="#0A2540" radius={[4, 4, 0, 0]} maxBarSize={22} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function computeStats(rows) {
  const pending = rows.filter((r) => !_isPaid(r.payment_status));
  const paid = rows.filter((r) => _isPaid(r.payment_status));

  const byCurrency = (list) => {
    const m = {};
    list.forEach((r) => {
      const cur = String(r.currency || '—').toUpperCase();
      m[cur] = (m[cur] || 0) + _num(r.amount);
    });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([currency, amount]) => ({ currency, amount }));
  };

  const cashierMap = {};
  pending.forEach((r) => {
    const name = r.payer_name || 'Unknown';
    if (!cashierMap[name]) cashierMap[name] = { name, amount: 0, count: 0, currency: String(r.currency || '').toUpperCase() };
    cashierMap[name].amount += _num(r.amount);
    cashierMap[name].count += 1;
  });
  const pendingCashiers = Object.values(cashierMap).sort((a, b) => b.amount - a.amount);
  const maxPendingCashierAmount = Math.max(...pendingCashiers.map((c) => c.amount), 0) || 1;

  const oldestPendingDays = pending.reduce((acc, r) => {
    const d = _daysAgo(r.order_date);
    if (d === null) return acc;
    return acc === null ? d : Math.max(acc, d);
  }, null);

  const trend = [];
  for (let i = 13; i >= 0; i -= 1) {
    const d = new Date(Date.now() - i * 86400000);
    const key = d.toISOString().slice(0, 10);
    trend.push({
      key,
      day: d.toLocaleDateString(undefined, { month: 'short', day: '2-digit' }),
      count: rows.filter((r) => String(r.order_date || '').slice(0, 10) === key).length,
    });
  }

  return {
    total: rows.length,
    pendingCount: pending.length,
    paidCount: paid.length,
    pendingByCurrency: byCurrency(pending),
    pendingCashiers,
    maxPendingCashierAmount,
    oldestPendingDays,
    trend,
  };
}

function KpiCard({ label, value, icon: Icon, sub }) {
  return (
    <div className="rounded-2xl border border-[#EDE6D8] bg-white p-4">
      <div className="flex items-start justify-between">
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#0A2540] font-semibold" style={{ fontFamily: 'JetBrains Mono, monospace' }}>{label}</p>
        {Icon && <Icon size={16} weight="duotone" className="text-[#C9A35B] shrink-0" />}
      </div>
      <p className="mt-2 text-2xl font-bold font-mono text-[#0A2540]">{value}</p>
      {sub && <p className="text-[11px] text-[#0A2540] mt-0.5 font-mono">{sub}</p>}
    </div>
  );
}

